import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useReviews, useThreadSources } from 'src/api/queries';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { ClaimIdChip } from 'src/components/editorial/ClaimIdChip';
import { ClaimThreadLane } from 'src/components/editorial/ClaimThreadLane';
import { Skeleton } from 'src/components/ui/Skeleton';
import { buildClaimThread, findOmissionReasons, type ThreadStation } from 'src/lib/claimThread';
import { LoadFailure } from 'src/components/editorial/LoadFailure';

/**
 * Answers: where did *this specific claim* come from and end up?
 *
 * Composition §12. The thread is assembled client-side from payloads the stream already has — the
 * join key is `claim_id` and no endpoint is needed for it.
 */
export function ClaimThreadWorkspace() {
  const { streamId = '', claimId = '' } = useParams<{ streamId: string; claimId: string }>();
  const { sources, isPending, error } = useThreadSources(streamId);
  const reviews = useReviews(streamId);

  const thread = useMemo(() => (isPending ? null : buildClaimThread(claimId, sources)), [claimId, sources, isPending]);
  const omissions = useMemo(() => findOmissionReasons(claimId, sources.outputs), [claimId, sources.outputs]);

  if (isPending) return <div className="page-medium"><Skeleton variant="rows" count={8} /></div>;

  if (error) {
    return (
      <div className="page-medium">
        <LoadFailure title="No se pudo armar el hilo" error={error} />
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="page-medium">
        <AbsenceState
          kind="empty"
          what={`El claim ${claimId} no está en las señales de esta transmisión.`}
          why="Un claim_id solo existe dentro del stream donde se normalizó."
          wouldChange="Revisa el listón de claims de la transmisión correcta."
        />
        <p>
          <Link to={`/stream/${encodeURIComponent(streamId)}/claims`}>Ver todos los claims de esta transmisión</Link>
        </p>
      </div>
    );
  }

  // Review records reference a draft, not a claim; resolving the count here keeps the thread
  // builder pure and free of a sixth data source.
  const draftIds = new Set(
    thread.stations
      .filter((station): station is Extract<ThreadStation, { stage: 'drafted'; reached: true }> =>
        station.reached && station.stage === 'drafted'
      )
      .flatMap(station => station.usages.map(usage => usage.transformationId))
  );
  const reviewCount = (reviews.data?.reviews ?? []).filter(review =>
    draftIds.has(review.transformation_id)
  ).length;

  const stations = thread.stations.map(station =>
    station.stage === 'reviewed' && reviewCount > 0
      ? ({ stage: 'reviewed', reached: true, reviewCount } satisfies ThreadStation)
      : station
  );

  return (
    <div className="page-medium">
      <header className="landmark thread__head">
        <p className="thread__eyebrow">Hilo del claim</p>
        <ClaimIdChip claimId={claimId} linkToThread={false} />
        <p className="thread__lede">
          Las estaciones que este claim no alcanzó se dibujan vacías. Ese hueco es el hallazgo.
        </p>
      </header>

      <ClaimThreadLane thread={{ ...thread, stations }} />

      {omissions.length > 0 && (
        <section className="quiet-tail">
          <h2>Por qué se omitió en algunos borradores</h2>
          <ul>
            {omissions.map(omission => (
              <li key={omission.profileId}>
                <span className="mono">{omission.profileId}</span> — {omission.reason}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
